import { McButton, McLoadingIndicator, McTable } from '@maersk-global/mds-react-wrapper';
import React, { Fragment, FunctionComponent, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import TablePlaceholder from '../../../common/components/TablePlaceholder/TablePlaceholder';
import { ModalContext, ModalType } from '../../../common/modals/ModalProvider';
import { eventsService } from '../../../common/service/events/events-service';
import { Event } from '../../../common/service/events/types';
import AffectedRenderer from '../affected-renderer';
import RemarksRenderer from '../remarks-renderer';
import { columns, mapString } from './consts';
import EventsDrawer from './events-drawer';
import { parseDate, parseDateOnly } from './helpers';

type EventsProps = {
  selectedTerminal: string;
  gateMode?: boolean;
};

const Events: FunctionComponent<EventsProps> = ({ selectedTerminal, gateMode }) => {
  const { showModal } = useContext(ModalContext);
  const [events, setEvents] = useState([] as Event[]);
  const [eventToAdd, setEventToAdd] = useState(false);
  const [eventToEdit, setEventToEdit] = useState<Event | undefined>(undefined);
  const [loading, setLoading] = useState(false);

  const service = useMemo(() => eventsService(), []);

  const fetch = async () => {
    setLoading(true);
    const resp = await service.fetchEvents(selectedTerminal);
    setEvents(resp || []);
    setLoading(false);
  };

  useEffect(() => {
    fetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- by design
  }, [selectedTerminal]);

  const handleDelete = async (id: string) => {
    await service.deleteEvent(id);
    fetch();
  };

  const dataMapper = useCallback(
    (data: Event[]) =>
      data
        .sort((a, b) => (b.toDateTime > a.toDateTime ? -1 : 1))
        .sort((a, b) => (b.fromDateTime > a.fromDateTime ? -1 : 1))
        .filter((x) => (gateMode ? x.gate === 'NO' || x.gate === 'PARTIAL' : true))
        .map((h) => ({
          id: h.id,
          name: h.name,
          remarks: h.remarks,
          gate: mapString(h.gate),
          vesselOps: mapString(h.vesselOps),
          fromDateTime: parseDateOnly(h.fromDateTime),
          toDateTime: parseDateOnly(h.toDateTime),
        })),
    [gateMode],
  );

  const eventsMapped = useMemo(() => dataMapper(events), [events, dataMapper]);

  return (
    <div className="Events">
      {loading ? (
        <McLoadingIndicator label="Fetching data..." />
      ) : (
        <div className="Events__table-wrapper">
          <div className="Events__tilte-button">
            <h2 className="Events__title">Other events</h2>
            {!gateMode ? <McButton label="Add new event" icon="plus" click={() => setEventToAdd(true)} /> : null}
          </div>
          {gateMode && !eventsMapped.length ? (
            <>
              <br />
              <div>N/A</div>
            </>
          ) : (
            <>
              <McTable columns={gateMode ? columns.slice(0, -1) : columns} datakey="id" data={eventsMapped}>
                {events.map((row) => (
                  <Fragment key={`${row.id}_actions`}>
                    <div slot={`${row.id}_gate`}>{<AffectedRenderer entity={row} type="gate" />}</div>
                    <div slot={`${row.id}_vesselOps`}>{<AffectedRenderer entity={row} type="vesselOps" />}</div>
                    <div slot={`${row.id}_actions`}>
                      <McButton
                        fit="small"
                        variant="plain"
                        appearance="primary"
                        icon="trash"
                        click={() => showModal(ModalType.DeleteHoliday, () => () => handleDelete(row.id))}
                      >
                        Delete
                      </McButton>
                      <McButton
                        fit="small"
                        variant="plain"
                        appearance="primary"
                        icon="pencil"
                        click={() => setEventToEdit(row)}
                      >
                        Edit
                      </McButton>
                    </div>
                    <div slot={`${row.id}_remarks`}>{<RemarksRenderer remarks={row.remarks} />}</div>
                  </Fragment>
                ))}
              </McTable>
              {events.length ? null : <TablePlaceholder placeholder="Create a new event to get started" />}
            </>
          )}
        </div>
      )}

      {(eventToAdd || eventToEdit) && (
        <EventsDrawer
          open={true}
          selectedTerminal={selectedTerminal}
          defaultValues={eventToEdit}
          action={eventToEdit ? 'edit' : 'add'}
          onSuccess={fetch}
          onClose={() => {
            setEventToEdit(undefined);
            setEventToAdd(false);
          }}
        />
      )}
    </div>
  );
};

export default Events;
